import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/index.css';
import '@/App.css';
import '@/i18n';

// Auth Components
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AuthCallback from '@/pages/AuthCallback';
import ResetPassword from '@/pages/ResetPassword';

// Main App Components
import Dashboard from '@/pages/Dashboard';
import PeptideDatabase from '@/pages/PeptideDatabase';
import PeptideDetail from '@/pages/PeptideDetail';
import Calculator from '@/pages/Calculator';
import Scheduler from '@/pages/Scheduler';
import Inventory from '@/pages/Inventory';
import Logger from '@/pages/Logger';
import Protocols from '@/pages/Protocols';
import Suppliers from '@/pages/Suppliers';
import Education from '@/pages/Education';
import Settings from '@/pages/Settings';
import Subscription from '@/pages/Subscription';
import Analytics from '@/pages/Analytics';
import Disclaimer from '@/pages/Disclaimer';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Achievements from '@/pages/Profile/Achievements';
import DailyCheckIn from '@/pages/Profile/DailyCheckIn';
import WaterIntake from '@/pages/Profile/WaterIntake';
import WeightTracker from '@/pages/Profile/WeightTracker';
import ArchivedVials from '@/pages/Profile/ArchivedVials';
import Resources from '@/pages/Profile/Resources';
import NotificationSettings from '@/pages/Profile/NotificationSettings';
import BackupRestore from '@/pages/Profile/BackupRestore';
import LabResults from '@/pages/Profile/LabResults';
import MacroTracker from '@/pages/Profile/MacroTracker';
import InsightsDashboard from '@/pages/Profile/InsightsDashboard';
import AIOptimization from '@/pages/AIOptimization';
import Marketplace from '@/pages/Marketplace';
import DataSync from '@/pages/Profile/DataSync';
import NeuroOptimization from '@/pages/NeuroOptimization';
import Forums from '@/pages/Forums';
import Collections from '@/pages/Collections';
import Admin from '@/pages/Admin';
import Leaderboard from '@/pages/Leaderboard';
import AIChat from '@/pages/AIChat';
import InstallPrompt from '@/components/InstallPrompt';

// Layout
import MainLayout from '@/components/Layout/MainLayout';
import { API } from '@/config/api';
import { startSyncEngine } from '@/services/offlineQueue';

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id in URL hash SYNCHRONOUSLY to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/peptides" element={<PeptideDatabase />} />
        <Route path="/peptides/:id" element={<PeptideDetail />} />
        <Route path="/calculator" element={<Calculator />} />
        <Route path="/scheduler" element={<Scheduler />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/logs" element={<Logger />} />
        <Route path="/protocols" element={<Protocols />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/education" element={<Education />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/subscription/success" element={<Subscription />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/check-in" element={<DailyCheckIn />} />
        <Route path="/water" element={<WaterIntake />} />
        <Route path="/weight" element={<WeightTracker />} />
        <Route path="/archived-vials" element={<ArchivedVials />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/notifications" element={<NotificationSettings />} />
        <Route path="/backup" element={<BackupRestore />} />
        <Route path="/lab-results" element={<LabResults />} />
        <Route path="/macros" element={<MacroTracker />} />
        <Route path="/insights" element={<InsightsDashboard />} />
          <Route path="/ai-optimize" element={<AIOptimization />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/sync" element={<DataSync />} />
          <Route path="/neuro" element={<NeuroOptimization />} />
          <Route path="/forums" element={<Forums />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/ai-chat" element={<AIChat />} />
      </Route>
    </Routes>
  );
}

function ProtectedRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Not authenticated');
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      {/* Global Disclaimer Banner */}
      <div className="disclaimer-banner" data-testid="disclaimer-banner">
        ⚠️ FOR RESEARCH USE ONLY. NOT FOR HUMAN CONSUMPTION. NOT MEDICAL ADVICE. VERIFY ALL COAs AND COMPLY WITH LOCAL LAWS.
      </div>
      <MainLayout user={user} />
    </div>
  );
}

function App() {
  useEffect(() => {
    // Set dark mode by default
    document.documentElement.classList.add('dark');
    // Boot the offline sync engine (online/offline listeners + periodic flush)
    const stop = startSyncEngine();
    return () => stop && stop();
  }, []);

  return (
    <div className="App">
      <Toaster />
      <BrowserRouter>
        <AppRouter />
        <InstallPrompt />
      </BrowserRouter>
    </div>
  );
}

export default App;
